import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Users, BarChart3, DollarSign, Calendar, Swords, Lock } from 'lucide-react';
import { formatBalance, formatUsdValueCompact } from '../utils/chartHelpers';
import { Line, Doughnut } from 'react-chartjs-2';
import { usePremiumStatus } from '../hooks/usePremiumStatus';

interface HolderDistributionProps {
  netuid: string | number;
}

interface CategoryData {
  count: number;
  total_balance: number;
  total_balance_usd: number;
}

interface HolderStats {
  categories: {
    Whale: CategoryData;
    Shark: CategoryData;
    Dolphin: CategoryData;
    Shrimp: CategoryData;
    [key: string]: CategoryData;
  };
  hhi: number;
  holders: number;
  timestamp: string;
  price?: number[];
  price_timestamps?: string[];
  pool_price?: string;
}

const getMiniChartData = (historyData: HolderStats[]) => {
  return {
    labels: historyData.map(entry => new Date(entry.timestamp)),
    datasets: [{
      data: historyData.map(entry => entry.holders),
      borderColor: 'rgba(6, 182, 212, 1)', // Changed to cyan
      backgroundColor: 'rgba(6, 182, 212, 0.1)', // Changed to cyan with transparency
      fill: true,
      tension: 0.4,
      borderWidth: 2
    }]
  };
};

const getHolderGrowthData = (historyData: HolderStats[]) => {
  return {
    labels: historyData.map(entry => new Date(entry.timestamp)),
    datasets: [
      {
        label: 'Total Holders',
        data: historyData.map(entry => entry.holders),
        borderColor: 'rgba(6, 182, 212, 1)', // Changed to cyan
        backgroundColor: 'rgba(6, 182, 212, 0.1)', // Changed to cyan with transparency
        fill: true,
        tension: 0.4,
        borderWidth: 2
      }
    ]
  };
};

const holderGrowthOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time' as const,
      time: {
        unit: 'day' as const,
        tooltipFormat: 'PPP',
        displayFormats: {
          day: 'MMM d'
        }
      },
      grid: {
        display: false
      },
      ticks: {
        display: false
      }
    },
    y: {
      display: false,
      grid: {
        display: false
      }
    }
  },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: 'rgba(255, 255, 255, 0.8)',
      bodyColor: 'rgba(255, 255, 255, 0.8)',
      callbacks: {
        title: (context: any) => {
          const date = new Date(context[0].parsed.x);
          return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        },
        label: (context: any) => {
          return `Holders: ${context.parsed.y.toLocaleString()}`;
        }
      }
    }
  },
  elements: {
    point: {
      radius: 0,
      hitRadius: 30,
      hoverRadius: 4
    }
  }
};

const calculateHolderChange = (historyData: HolderStats[]): number => {
  if (historyData.length < 2) return 0;
  
  const oldestValue = historyData[0].holders;
  const newestValue = historyData[historyData.length - 1].holders;
  
  return ((newestValue - oldestValue) / oldestValue) * 100;
};

const miniChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false }
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false }
  },
  elements: {
    point: { radius: 0 },
    line: { tension: 0.4 }
  }
};

const filterDataByTimeRange = (data: any[], timeRange: string) => {
  if (timeRange === '1D') {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return data.filter(entry => new Date(entry.timestamp) >= oneDayAgo);
  }
  return data;
};

export default function HolderDistribution({ netuid }: HolderDistributionProps) {
  const { isPremium, loading: checkingPremium } = usePremiumStatus();
  const [stats, setStats] = useState<HolderStats | null>(null);
  const [historyData, setHistoryData] = useState<HolderStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryChanges, setCategoryChanges] = useState<Record<string, number>>({});
  const [timeRange, setTimeRange] = useState<'1D' | 'all'>('1D');

  useEffect(() => {
    const fetchNetuidStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const entriesCollectionRef = collection(db, 'netuid_stats', netuid.toString(), 'entries');
        const entriesQuery = query(entriesCollectionRef, orderBy('timestamp', 'asc'));
        const entriesSnapshot = await getDocs(entriesQuery);

        if (!entriesSnapshot.empty) {
          const historyEntries = entriesSnapshot.docs.map(doc => {
            const data = doc.data() as HolderStats;
            const combinedData = combineSharkAndWhale(data);
            return combinedData;
          });
          
          const filteredEntries = filterDataByTimeRange(historyEntries, timeRange);
          setHistoryData(filteredEntries);
          
          const latestEntry = filteredEntries[filteredEntries.length - 1];
          setStats(latestEntry);
          
          if (filteredEntries.length >= 2) {
            const oldestEntry = filteredEntries[0];
            const newestEntry = filteredEntries[filteredEntries.length - 1];
            
            const changes: Record<string, number> = {};
            
            ['Whale', 'Dolphin', 'Shrimp'].forEach(category => {
              if (oldestEntry.categories[category] && newestEntry.categories[category]) {
                const oldCount = oldestEntry.categories[category].count;
                const newCount = newestEntry.categories[category].count;
                
                if (oldCount > 0) {
                  changes[category] = ((newCount - oldCount) / oldCount) * 100;
                } else {
                  changes[category] = newCount > 0 ? 100 : 0;
                }
              }
            });
            
            setCategoryChanges(changes);
          }
        } else {
          const statsDocRef = doc(db, 'netuid_stats', netuid.toString());
          const statsDoc = await getDoc(statsDocRef);
          
          if (statsDoc.exists()) {
            const statsData = statsDoc.data() as HolderStats;
            const combinedData = combineSharkAndWhale(statsData);
            setStats(combinedData);
            setHistoryData([combinedData]);
          } else {
            setError('No holder distribution data available for this subnet');
          }
        }
      } catch (err) {
        console.error('Error fetching netuid stats:', err);
        setError('Failed to load on-chain data');
      } finally {
        setLoading(false);
      }
    };

    if (netuid) {
      fetchNetuidStats();
    }
  }, [netuid, timeRange]);

  const combineSharkAndWhale = (data: HolderStats): HolderStats => {
    const result = { ...data };
    
    if (result.categories.Shark && result.categories.Whale) {
      result.categories.Whale = {
        count: (result.categories.Whale.count || 0) + (result.categories.Shark.count || 0),
        total_balance: (result.categories.Whale.total_balance || 0) + (result.categories.Shark.total_balance || 0),
        total_balance_usd: (result.categories.Whale.total_balance_usd || 0) + (result.categories.Shark.total_balance_usd || 0)
      };
      
      delete result.categories.Shark;
    }
    
    return result;
  };

  if (loading || checkingPremium) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
        <div className="flex items-center space-x-2 mb-4">
          <Users className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold">Wallet Analytics</h2>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-black/40 rounded-lg w-48"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-black/40 rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-black/40 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="relative bg-black/20 backdrop-blur-sm rounded-lg border border-white/5">
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm rounded-lg">
          <div className="flex flex-col items-center text-center p-8">
            <div className="relative mb-6">
              <Swords className="w-16 h-16 text-cyan-400" />
              <Lock className="w-8 h-8 text-cyan-400 absolute -bottom-2 -right-2" />
            </div>
            <h3 className="text-2xl font-bold mb-2">Premium Access Only</h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Stake 10,000 $PULSE tokens to unlock premium features including detailed wallet analytics.
            </p>
            <a
              href="https://creator.bid/agents/676ef7b23bdebce80077f770"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <Lock className="w-5 h-5" />
              Unlock Premium Access
            </a>
          </div>
        </div>

        <div className="opacity-10 pointer-events-none p-4">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold">Wallet Analytics</h2>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              <span className="text-xl font-semibold">1,267 Holders</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
                <div className="h-24"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
        <div className="flex items-center space-x-2 mb-4">
          <Users className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold">Wallet Analytics</h2>
        </div>
        <div className="text-gray-400 text-center py-4">{error}</div>
      </div>
    );
  }

  if (!stats || !stats.categories || historyData.length === 0) {
    return (
      <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
        <div className="flex items-center space-x-2 mb-4">
          <Users className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold">Wallet Analytics</h2>
        </div>
        <div className="text-gray-400 text-center py-4">No holder distribution data available</div>
      </div>
    );
  }

  const lastUpdated = stats.timestamp 
    ? new Date(stats.timestamp).toLocaleString() 
    : 'Unknown';

  const categoryOrder = ['Shrimp', 'Dolphin', 'Whale'];

  const getCategoryEmoji = (category: string) => {
    switch (category) {
      case 'Whale':
        return '🐋';
      case 'Dolphin':
        return '🐬';
      case 'Shrimp':
        return '🦐';
      default:
        return '👤';
    }
  };

  const combinedChartData = {
    labels: historyData.map(entry => new Date(entry.timestamp)),
    datasets: [
      {
        label: 'Whale',
        data: historyData.map(entry => entry.categories.Whale?.count || 0),
        backgroundColor: 'rgba(6, 182, 212, 0.3)', // Changed to cyan
        borderColor: 'rgba(6, 182, 212, 1)', // Changed to cyan
        borderWidth: 1,
        fill: true,
        tension: 0.1,
        yAxisID: 'y-holders',
        order: 1
      },
      {
        label: 'Dolphin',
        data: historyData.map(entry => entry.categories.Dolphin?.count || 0),
        backgroundColor: 'rgba(46, 204, 113, 0.3)',
        borderColor: 'rgba(46, 204, 113, 1)',
        borderWidth: 1,
        fill: true,
        tension: 0.1,
        yAxisID: 'y-holders',
        order: 1
      },
      {
        label: 'Shrimp',
        data: historyData.map(entry => entry.categories.Shrimp?.count || 0),
        backgroundColor: 'rgba(155, 89, 182, 0.3)',
        borderColor: 'rgba(155, 89, 182, 1)',
        borderWidth: 1,
        fill: true,
        tension: 0.1,
        yAxisID: 'y-holders',
        order: 1
      },
      {
        label: 'Price',
        data: historyData.map(entry => {
          let price = 0;
          if (entry.pool_price) {
            price = parseFloat(entry.pool_price);
          } else if (entry.price && entry.price.length > 0) {
            price = entry.price[0];
          }
          return price > 0 ? price : null;
        }),
        borderColor: 'rgba(6, 182, 212, 1)', // Changed to cyan
        backgroundColor: 'rgba(6, 182, 212, 0.1)', // Changed to cyan with transparency
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        yAxisID: 'y-price',
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 30,
        type: 'line' as const,
        order: 0,
        spanGaps: true
      }
    ]
  };

  const combinedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false,
        type: 'time' as const,
        time: {
          unit: 'day' as const,
          tooltipFormat: 'PPP',
          displayFormats: {
            day: 'MMM d'
          }
        }
      },
      'y-holders': {
        display: false,
        type: 'linear' as const,
        position: 'left' as const,
        stacked: true
      },
      'y-price': {
        display: false,
        type: 'linear' as const,
        position: 'right' as const
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          },
          padding: window.innerWidth < 768 ? 10 : 12,
          usePointStyle: true
        },
        reverse: true
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        titleColor: 'rgba(255, 255, 255, 0.9)',
        bodyColor: 'rgba(255, 255, 255, 0.9)',
        padding: window.innerWidth < 768 ? 10 : 14,
        boxPadding: window.innerWidth < 768 ? 6 : 8,
        usePointStyle: true,
        position: 'nearest' as const,
        titleFont: {
          size: window.innerWidth < 768 ? 12 : 14,
          weight: 'bold'
        },
        bodyFont: {
          size: window.innerWidth < 768 ? 11 : 13
        },
        callbacks: {
          title: (context: any) => {
            const date = new Date(context[0].parsed.x);
            return date.toLocaleString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          },
          label: (context: any) => {
            const datasetLabel = context.dataset.label;
            const value = context.parsed.y;
            
            if (datasetLabel === 'Price') {
              return `💰 Price: $${value.toFixed(4)}`;
            }
            
            const dataIndex = context.dataIndex;
            const entry = historyData[dataIndex];
            
            if (!entry || !entry.categories[datasetLabel]) {
              return `${datasetLabel}: ${value}`;
            }
            
            const percentage = ((value / entry.holders) * 100).toFixed(1);
            const formattedValue = value.toLocaleString();
            const emoji = getCategoryEmoji(datasetLabel);
            
            const categoryData = entry.categories[datasetLabel];
            const balanceFormatted = categoryData.total_balance.toFixed(2);
            const usdValueFormatted = categoryData.total_balance_usd.toFixed(2);
            
            return [
              `${emoji} ${datasetLabel}s: ${formattedValue} (${percentage}%)`,
              `Balance: ${balanceFormatted} α`,
              `Value: $${usdValueFormatted}`
            ];
          },
          footer: (context: any) => {
            const dataIndex = context[0].dataIndex;
            const entry = historyData[dataIndex];
            return [`Total Holders: ${entry.holders.toLocaleString()}`];
          }
        }
      }
    },
    elements: {
      point: {
        radius: 0,
        hitRadius: 30,
        hoverRadius: 5,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        borderColor: 'rgba(0, 0, 0, 0.8)',
        borderWidth: 2
      },
      line: {
        tension: 0.1
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold">Wallet Analytics</h2>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-xs text-gray-400">
            Last updated: {lastUpdated}
          </div>
          <div className="flex items-center space-x-1 bg-black/20 rounded-lg p-1">
            <button
              onClick={() => setTimeRange('1D')}
              className={`px-2 py-1 rounded text-xs ${
                timeRange === '1D' ? 'bg-cyan-600 text-white' : 'text-gray-400'
              }`}
            >
              1D
            </button>
            <button
              onClick={() => setTimeRange('all')}
              className={`px-2 py-1 rounded text-xs ${
                timeRange === 'all' ? 'bg-cyan-600 text-white' : 'text-gray-400'
              }`}
            >
              All
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" />
          <span className="text-xl font-semibold">
            {stats.holders.toLocaleString()} Holders
          </span>
          {historyData.length >= 2 && (
            <span className={`text-sm font-medium ${
              calculateHolderChange(historyData) >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {calculateHolderChange(historyData) >= 0 ? '↑' : '↓'} 
              {Math.abs(calculateHolderChange(historyData)).toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      <div className="flex gap-6">
        <div className="flex flex-col gap-4 w-1/2">
          {categoryOrder.map(category => {
            const data = stats.categories[category];
            if (!data) return null;
            
            const totalBalance = Object.values(stats.categories).reduce(
              (sum, category) => sum + category.total_balance, 0
            );
            const balancePercentage = totalBalance > 0 ? (data.total_balance / totalBalance) * 100 : 0;
            
            const percentChange = categoryChanges[category] || 0;
            const changeDisplay = percentChange !== 0 ? (
              <span className={`text-xs ${percentChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {percentChange > 0 ? '↑' : '↓'} {Math.abs(percentChange).toFixed(1)}%
              </span>
            ) : null;
            
            const categoryLabel = category === 'Whale' ? 'Whales' : `${category}s`;
            
            return (
              <div key={category} className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5 hover:bg-black/40 transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl" role="img" aria-label={category}>{getCategoryEmoji(category)}</span>
                    <span className="text-sm font-medium text-gray-300">{categoryLabel}</span>
                  </div>
                  {changeDisplay}
                </div>
                <p className="text-lg font-bold">{data.count.toLocaleString()}</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-400 flex justify-between">
                    <span>Balance:</span>
                    <span className="font-medium">{data.total_balance.toFixed(2)} α</span>
                  </p>
                  <p className="text-xs text-gray-400 flex justify-between">
                    <span>Value:</span>
                    <span className="font-medium">${data.total_balance_usd.toFixed(2)}</span>
                  </p>
                  <p className="text-xs text-gray-400 flex justify-between">
                    <span>% of circulating supply:</span>
                    <span className="font-medium">{balancePercentage.toFixed(2)}%</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="w-1/2 space-y-4">
          <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
            <h3 className="text-sm font-medium mb-2">Holder Growth</h3>
            <div className="h-[150px]">
              <Line 
                data={getHolderGrowthData(historyData)} 
                options={holderGrowthOptions}
              />
            </div>
          </div>

          <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
            <h3 className="text-sm font-medium mb-2">Distribution by Balance</h3>
            <div className="h-[200px]">
              <Doughnut 
                data={{
                  labels: categoryOrder.map(cat => cat === 'Whale' ? 'Whales' : `${cat}s`),
                  datasets: [{
                    data: categoryOrder.map(cat => stats.categories[cat]?.total_balance || 0),
                    backgroundColor: [
                      'rgba(155, 89, 182, 0.8)',
                      'rgba(46, 204, 113, 0.8)',
                      'rgba(6, 182, 212, 0.8)', // Changed to cyan
                    ],
                    borderColor: [
                      'rgba(155, 89, 182, 1)',
                      'rgba(46, 204, 113, 1)',
                      'rgba(6, 182, 212, 1)', // Changed to cyan
                    ],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom' as const,
                      labels: {
                        color: 'rgba(255, 255, 255, 0.8)',
                        font: {
                          size: window.innerWidth < 768 ? 10 : 12
                        },
                        padding: window.innerWidth < 768 ? 10 : 12,
                        usePointStyle: true
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          const value = context.raw as number;
                          const total = (context.dataset.data as number[]).reduce((a, b) => a + b, 0);
                          const percentage = ((value / total) * 100).toFixed(1);
                          return `${context.label}: ${value.toFixed(2)} α (${percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
        <h3 className="text-sm font-medium mb-2">Wallet Distribution</h3>
        <div className="h-[350px]">
          <Line data={combinedChartData} options={combinedChartOptions} />
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-400">
        <p>
          <strong>HHI (Herfindahl-Hirschman Index):</strong> Measures market concentration. 
          Lower values indicate more distributed holdings.
        </p>
        <p className="mt-1">
          <strong>Categories:</strong> 🦐 Shrimp ($1 - $1,000), 🐬 Dolphin ($1,000 - $10,000), 
          🐋 Whales ($10,000+)
        </p>
      </div>
    </div>
  );
}

export { HolderDistribution }